/**
 * File-backed resources (S3-backed, tracked in toolbox_db's `resource` table).
 *
 * A file resource is reference material uploaded once and referenced from anywhere — a journey
 * map step, a lab, a product page. That is the difference from `support/journeyMap/files.ts`,
 * whose attachments belong to one journey map and are namespaced per map in storage.
 *
 * Upload is the same three-step handshake, so the bytes never pass through the API:
 *
 *   1. POST `db/resource-file/presign/`  -> short-lived signed PUT + object key
 *   2. PUT the file straight at storage with the content type the presign returned
 *   3. POST `db/resource-file/`          -> creates the row; the server independently re-reads
 *                                           the object's real size and content type off
 *                                           storage before saving
 *
 * Step 3 is what makes step 2 trustworthy: a client cannot invent an object key or misreport a
 * file's size, because the row is only written after the server has looked at the object. The
 * server also refuses keys outside the resource-file prefix, so another namespace's object
 * cannot be registered here.
 *
 * `uploadResourceFile` falls back to a single multipart POST when the direct PUT fails, which
 * is what happens behind a proxy or CSP that blocks cross-origin PUTs.
 *
 * Reads are signed GET URLs fetched per click (`db/resource-file/{id}/download-url/`). They
 * expire, so they are never held in component state — call `getResourceFileDownload` at the
 * point of use and let it go.
 */
import { buildApiUrl, getApiHeaders } from './queriesHelpers';
import { UploadProgressCallback, putFileToStorageWithProgress } from './xhrUpload';

/**
 * How the UI should display a file. Decided by the backend
 * (`db/services/resource_files.py::render_kind_for`) rather than re-derived here, so the
 * browser and the API cannot disagree about what a given content type is.
 *
 * `download` means "offer the bytes, do not try to render them".
 */
export type ResourceRenderKind =
  | 'pdf'
  | 'markdown'
  | 'spreadsheet'
  | 'presentation'
  | 'image'
  | 'text'
  | 'video'
  | 'download'
  | '';

export type ResourceFileRecord = {
  id: number;
  name: string;
  description?: string | null;
  type?: string | null;
  provider?: string | null;
  external?: boolean;
  url?: string | null;

  file_name?: string | null;
  object_key?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  version?: number;
  storage_bucket?: string | null;
  is_active?: boolean;
  metadata?: Record<string, any> | null;

  // Server-derived, read-only.
  is_file?: boolean;
  is_previewable?: boolean;
  render_kind?: ResourceRenderKind;

  updated_by?: string | null;
  last_updated?: string | null;
};

export type ResourceFilePresign = {
  upload_url: string;
  object_key: string;
  content_type: string;
  file_name: string;
  render_kind: ResourceRenderKind;
  expires_in: number;
  max_bytes: number;
};

export type ResourceFileDownload = {
  url: string;
  expires_in: number;
  refresh_after: number;
  signed: boolean;
  resource_id: number;
  file_name: string;
  content_type: string;
  size_bytes: number;
  version: number;
  render_kind: ResourceRenderKind;
};

export type ResourceFileListResult = {
  items: ResourceFileRecord[];
  total_count: number;
  offset: number;
  page_size: number;
};

/**
 * How far along the server-generated PDF preview is for a deck the browser cannot render itself.
 *
 *   ready       — the PDF derivative exists; `preview_url` points at it and renders inline.
 *   processing  — it is a presentation and the conversion has been enqueued on the worker but the
 *                 derivative is not ready yet; `preview_url` is null. Re-request shortly.
 *   unavailable — no preview will be produced (non-presentation file, storage unconfigured, or a
 *                 lookup/enqueue failure); `preview_url` is null and the caller keeps the download.
 *
 * The Go runtime serves the same `preview-url` endpoint and returns the identical shape/strings.
 */
export type ResourceFilePreviewStatus = 'ready' | 'processing' | 'unavailable';

/**
 * A server-generated PDF preview for a file the browser cannot render itself (PowerPoint decks).
 *
 * The conversion runs asynchronously in the toolbox_queue worker (LibreOffice is not on the API
 * pods), so a first request for an unconverted deck comes back `processing` with a null
 * `preview_url`, not a blocking wait. `preview_url` is only set when `preview_status === 'ready'`;
 * a null is never an error — the viewer shows a "preparing" note (`processing`) or falls back to
 * the download-only message (`unavailable`).
 */
export type ResourceFilePreview = {
  resource_id: number;
  render_kind: ResourceRenderKind;
  preview_url: string | null;
  preview_status: ResourceFilePreviewStatus;
};

/** The S3 bucket is unset, so the whole file-resource feature is inert (API answers 503). */
export class ResourceFileStorageUnavailableError extends Error {}

const LIST_ENDPOINT = 'db/resource-file/';

const resourceFileEndpoint = (resourceId: number | string) => `db/resource-file/${resourceId}/`;

/**
 * Every resource-file endpoint normalizes its error body to `detail`, but field-level
 * serializer errors still come back keyed by field, so check those too.
 */
const errorMessage = (payload: any, status: number, fallback: string): string => {
  const detail = payload?.detail ?? payload?.object_key ?? payload?.file_name ?? payload?.name;

  if (Array.isArray(detail) && detail.length) return String(detail[0]);
  if (detail) return String(detail);
  if (typeof payload === 'string' && payload.trim()) return payload;

  return `${fallback} (${status})`;
};

async function resourceFileApiFetch<T = any>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  endpoint: string,
  options: { params?: Record<string, any>; body?: any; form?: FormData } = {},
): Promise<T> {
  const { params = {}, body, form } = options;

  const headers = { ...getApiHeaders(null, method, endpoint) } as Record<string, string>;

  // FormData sets its own multipart boundary, so the JSON content type must not be sent.
  if (form) delete headers['Content-Type'];

  const response = await fetch(buildApiUrl(endpoint, params, true), {
    method,
    headers,
    credentials: 'include',
    ...(form ? { body: form } : {}),
    ...(body === undefined || form ? {} : { body: JSON.stringify(body) }),
  });

  const text = await response.text();

  let parsed: any = null;

  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const message = errorMessage(parsed, response.status, 'Resource file request failed');

    if (response.status === 503) {
      throw new ResourceFileStorageUnavailableError(message);
    }

    const error: any = new Error(message);
    error.status = response.status;
    error.payload = parsed;
    throw error;
  }

  return parsed as T;
}

/**
 * The library listing behind the file picker: registered file resources, searchable.
 *
 * This is the "look it up in storage" path, and it deliberately reads the database rather than
 * the bucket. Listing S3 directly would surface objects with no row behind them, and therefore
 * no verified content type, size or display name — the exact state the upload handshake exists
 * to prevent.
 */
export async function listResourceFiles(
  params: {
    search?: string;
    renderKind?: ResourceRenderKind;
    /** Hide a single render kind server-side (e.g. keep videos out of the general file picker). */
    excludeRenderKind?: ResourceRenderKind;
    includeInactive?: boolean;
    offset?: number;
    pageSize?: number;
  } = {},
): Promise<ResourceFileListResult> {
  const query: Record<string, any> = {};

  if (params.search?.trim()) query.search = params.search.trim();
  if (params.renderKind) query.render_kind = params.renderKind;
  if (params.excludeRenderKind) query.exclude_render_kind = params.excludeRenderKind;
  if (params.includeInactive) query.include_inactive = 1;
  if (params.offset != null) query.offset = params.offset;
  if (params.pageSize != null) query.page_size = params.pageSize;

  const payload = await resourceFileApiFetch<any>('GET', LIST_ENDPOINT, { params: query });

  if (Array.isArray(payload)) {
    return {
      items: payload as ResourceFileRecord[],
      total_count: payload.length,
      offset: 0,
      page_size: payload.length,
    };
  }

  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    total_count: Number(payload?.total_count ?? 0),
    offset: Number(payload?.offset ?? 0),
    page_size: Number(payload?.page_size ?? 0),
  };
}

export async function getResourceFile(
  resourceId: number | string,
): Promise<ResourceFileRecord> {
  return await resourceFileApiFetch<ResourceFileRecord>('GET', resourceFileEndpoint(resourceId));
}

export async function presignResourceFileUpload(file: File): Promise<ResourceFilePresign> {
  return await resourceFileApiFetch<ResourceFilePresign>('POST', 'db/resource-file/presign/', {
    body: {
      file_name: file.name,
      content_type: file.type || 'application/octet-stream',
    },
  });
}

/**
 * PUT the file at the presigned URL. Uses XHR (not fetch) so `onProgress` can report real
 * upload progress; the signature still covers the method, key and content type only, so we send
 * no API auth headers (that would leak the Toolbox token to storage and invalidate the signature).
 */
async function putToStorage(
  presign: ResourceFilePresign,
  file: File,
  onProgress?: UploadProgressCallback,
): Promise<void> {
  await putFileToStorageWithProgress(
    presign.upload_url,
    file,
    presign.content_type || file.type || 'application/octet-stream',
    onProgress,
  );
}

type UploadOptions = {
  /** Display name for the resource. Defaults to the file name. */
  name?: string;
  description?: string;
  /** Reports upload progress (0–100) for a determinate progress bar. */
  onProgress?: UploadProgressCallback;
};

/** Register *file* as a new file resource, uploading the bytes first. */
export async function uploadResourceFile(
  file: File,
  options: UploadOptions = {},
): Promise<ResourceFileRecord> {
  const presign = await presignResourceFileUpload(file);

  if (presign.max_bytes && file.size > presign.max_bytes) {
    throw new Error(
      `${file.name} is larger than the ${Math.round(presign.max_bytes / (1024 * 1024))} MB limit.`,
    );
  }

  try {
    await putToStorage(presign, file, options.onProgress);
  } catch {
    // A blocked direct PUT is a network/policy problem, not a bad file: let the API stream it.
    return await uploadResourceFileMultipart(file, options);
  }

  const payload: Record<string, any> = {
    object_key: presign.object_key,
    file_name: presign.file_name || file.name,
    name: options.name?.trim() || presign.file_name || file.name,
  };

  if (options.description) payload.description = options.description;

  return await resourceFileApiFetch<ResourceFileRecord>('POST', LIST_ENDPOINT, { body: payload });
}

/** Single-request fallback: the API receives the bytes and writes them to storage itself. */
async function uploadResourceFileMultipart(
  file: File,
  options: UploadOptions = {},
): Promise<ResourceFileRecord> {
  const form = new FormData();
  form.append('file', file);
  form.append('file_name', file.name);
  form.append('name', options.name?.trim() || file.name);

  if (options.description) form.append('description', options.description);

  return await resourceFileApiFetch<ResourceFileRecord>('POST', LIST_ENDPOINT, { form });
}

/**
 * Replace the bytes behind an existing file resource.
 *
 * A new `object_key` on an existing row is what the API reads as a replacement: it bumps
 * `version` and removes the superseded object, so every item referencing this resource id
 * keeps working and starts serving the new content.
 */
export async function replaceResourceFile(
  resourceId: number | string,
  file: File,
  onProgress?: UploadProgressCallback,
): Promise<ResourceFileRecord> {
  const presign = await presignResourceFileUpload(file);
  await putToStorage(presign, file, onProgress);

  return await resourceFileApiFetch<ResourceFileRecord>(
    'PATCH',
    resourceFileEndpoint(resourceId),
    {
      body: {
        object_key: presign.object_key,
        file_name: presign.file_name || file.name,
      },
    },
  );
}

export async function updateResourceFile(
  resourceId: number | string,
  changes: { name?: string; description?: string; file_name?: string; is_active?: boolean },
): Promise<ResourceFileRecord> {
  return await resourceFileApiFetch<ResourceFileRecord>(
    'PATCH',
    resourceFileEndpoint(resourceId),
    { body: changes },
  );
}

/** Remove the object and deactivate the row. The API refuses if the object survives. */
export async function deleteResourceFile(resourceId: number | string): Promise<void> {
  await resourceFileApiFetch<null>('DELETE', resourceFileEndpoint(resourceId));
}

export async function getResourceFileDownload(
  resourceId: number | string,
  options: { inline?: boolean } = {},
): Promise<ResourceFileDownload> {
  return await resourceFileApiFetch<ResourceFileDownload>(
    'GET',
    `db/resource-file/${resourceId}/download-url/`,
    { params: options.inline ? { inline: 1 } : {} },
  );
}

/**
 * Fetch the state of a PowerPoint deck's server-generated PDF preview.
 *
 * The conversion is asynchronous (toolbox_queue worker), so this returns one of three
 * `preview_status` values — `ready` with a signed `preview_url` the PDF iframe renders,
 * `processing` (enqueued, not ready yet — re-request shortly), or `unavailable` (no preview will
 * be produced; the caller keeps the download-only fallback). `preview_url` is non-null only when
 * `ready`.
 */
export async function getResourceFilePreviewUrl(
  resourceId: number | string,
): Promise<ResourceFilePreview> {
  return await resourceFileApiFetch<ResourceFilePreview>(
    'GET',
    `db/resource-file/${resourceId}/preview-url/`,
  );
}

/**
 * Fetch a resource file's bytes THROUGH the API (same-origin), for the viewers that must read
 * the content into JS to render it (markdown, text, spreadsheet, presentation).
 *
 * The signed-URL path (`getResourceFileDownload`) hands back an S3 URL the browser would have to
 * fetch cross-origin, which needs a CORS rule on the bucket; this endpoint streams the object
 * back from `db/resource-file/{id}/download/` so no bucket CORS is required. It goes through the
 * same `getApiHeaders` auth as every other API call — which is also why it is only usable from a
 * `fetch` (an `<iframe>`/`<img>` src cannot carry the auth headers), so PDF and image kinds keep
 * using the signed URL and load directly in the browser.
 *
 * Returns the raw `Response` so the caller can pick `.text()` / `.arrayBuffer()` per render kind.
 */
export async function fetchResourceFileContent(
  resourceId: number | string,
): Promise<Response> {
  const endpoint = `db/resource-file/${resourceId}/download/`;
  const headers = { ...getApiHeaders(null, 'GET', endpoint) } as Record<string, string>;

  const response = await fetch(buildApiUrl(endpoint, {}, true), {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let message = `Resource file request failed (${response.status})`;
    try {
      const payload = await response.clone().json();
      message = errorMessage(payload, response.status, 'Resource file request failed');
    } catch {
      /* non-JSON body (e.g. the streamed bytes on an unexpected 200 path) — keep the fallback */
    }

    if (response.status === 503) {
      throw new ResourceFileStorageUnavailableError(message);
    }

    const error: any = new Error(message);
    error.status = response.status;
    throw error;
  }

  return response;
}

/** Human-readable size, for file cards and picker rows. */
export function formatFileSize(sizeBytes?: number | null): string {
  const size = Number(sizeBytes || 0);

  if (!size) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;

  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Trigger a browser download for a resource file.
 *
 * Uses an anchor rather than `window.open` so the `Content-Disposition: attachment` the signed
 * URL carries is honoured without a popup blocker getting involved.
 */
export async function downloadResourceFile(resourceId: number | string): Promise<void> {
  const download = await getResourceFileDownload(resourceId);

  const anchor = document.createElement('a');
  anchor.href = download.url;
  anchor.rel = 'noopener';
  if (download.file_name) anchor.download = download.file_name;

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

/**
 * Download several resource files.
 *
 * Sequential on purpose: each download needs its own signed URL, and firing a burst of
 * synthetic anchor clicks in one tick gets throttled as a popup/download flood by every
 * browser. Failures are collected rather than thrown so one bad file does not cancel the rest.
 */
export async function downloadResourceFiles(
  resourceIds: Array<number | string>,
): Promise<{ downloaded: Array<number | string>; failed: Array<{ id: number | string; error: string }> }> {
  const downloaded: Array<number | string> = [];
  const failed: Array<{ id: number | string; error: string }> = [];

  for (const resourceId of resourceIds) {
    try {
      await downloadResourceFile(resourceId);
      downloaded.push(resourceId);
    } catch (error: any) {
      failed.push({ id: resourceId, error: String(error?.message || error) });
    }
  }

  return { downloaded, failed };
}
